import {
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

import { Box } from '@mui/material';
import clsx from 'clsx';
import {
  ResponsiveContainer,
  Treemap,
  type TreemapContentType,
  type TreemapNode,
} from 'recharts';

import { getTreeMapTooltipPosition } from './_utils';
import { MIN_RENDERABLE_SIZE, TreeMapCell } from './TreeMapCell';
import { TreeMapCellRenderProps, TreeMapItem, TreeMapProps } from './types';
import { tokens } from '../../../../theme/generated/tokens';
import { classNamesFor } from '../../../../utils/classNames';
import { mergeSx } from '../../../../utils';

const DEFAULT_HEIGHT = 280;
// Fixed 4:3 aspect ratio — not exposed as a prop since layout direction
// is an implementation detail of the recharts engine.
const ASPECT_RATIO = 4 / 3;
// Fallback color when no color is supplied on a data item.
const DEFAULT_CELL_COLOR = tokens.primitives.colors.blue[100];
const TOOLTIP_VIEWPORT_MARGIN = 8;

const treeMapClasses = {
  root: classNamesFor('TreeMap').root(),
};

type TooltipState = {
  content: ReactNode;
  x: number;
  y: number;
};

// recharts spreads all data item fields onto the node, so our TreeMapItem
// fields are available alongside the standard TreemapNode geometry props.
type RechartsNodeWithItem = TreemapNode & Partial<TreeMapItem>;

type ContentRendererArgs = {
  node: RechartsNodeWithItem;
  hoveredId: string | null;
  onHoverChange: (id: string | null) => void;
  onCellClick?: (id: string, event?: MouseEvent<Element>) => void;
  onMouseMove?: (id: string, clientX: number, clientY: number) => void;
  renderCell?: (props: TreeMapCellRenderProps) => ReactNode;
};

// Returns a ReactElement (never null) as required by TreemapContentType.
const renderTreeMapCell = ({
  node,
  hoveredId,
  onHoverChange,
  onCellClick,
  onMouseMove,
  renderCell,
}: ContentRendererArgs): ReactElement => {
  const { x = 0, y = 0, width = 0, height = 0, children } = node;

  // recharts passes group (parent) nodes too — skip them.
  if (
    children?.length ||
    !node.id ||
    width < MIN_RENDERABLE_SIZE ||
    height < MIN_RENDERABLE_SIZE
  ) {
    // Return an empty group so recharts' React.ReactElement constraint is met.
    return <g />;
  }

  const item: TreeMapItem = {
    id: String(node.id),
    name: node.name ?? '',
    value: node.value ?? 0,
    color: node.color ?? DEFAULT_CELL_COLOR,
    textColor: node.textColor,
    subtitle: node.subtitle,
    footer: node.footer,
    footerColor: node.footerColor,
    borderColor: node.borderColor,
    borderDashed: node.borderDashed,
    loading: node.loading,
    tooltip: node.tooltip,
  };

  const isHovered = hoveredId === item.id;

  if (renderCell) {
    return <g>{renderCell({ item, x, y, width, height, isHovered })}</g>;
  }

  return (
    <TreeMapCell
      {...item}
      x={x}
      y={y}
      width={width}
      height={height}
      isHovered={isHovered}
      onCellClick={onCellClick}
      onHoverChange={onHoverChange}
      onMouseMove={onMouseMove}
    />
  );
};

/**
 * TreeMap chart — renders proportional area cells using recharts' Treemap layout.
 *
 * Each `TreeMapItem` in `data` becomes one interactive cell sized by its `value`.
 * The default cell renderer displays name, subtitle, and footer labels with a
 * loading spinner variant. Pass `renderCell` to take full control of cell rendering.
 *
 * Tooltips are passive cursor-followers (`pointer-events: none`), matching other
 * chart tooltips. Put actions on the cell click/keyboard handler, not inside the
 * tooltip content.
 *
 * @example
 * <TreeMap
 *   data={[{ id: '1', name: 'Cluster A', value: 120, color: tokens.theme.data.blue800 }]}
 *   onCellClick={(id) => navigate(`/clusters/${id}`)}
 * />
 */
export const TreeMap = ({
  data,
  height = DEFAULT_HEIGHT,
  onCellClick,
  renderCell,
  testId = 'tree-map',
  sx,
  className,
}: TreeMapProps) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [tooltipState, setTooltipState] = useState<TooltipState | null>(null);
  const [tooltipOffset, setTooltipOffset] = useState({ left: 0, top: -9999 });
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const clearTooltip = () => {
    setHoveredId(null);
    setTooltipState(null);
  };

  const handleHoverChange = (id: string | null) => {
    setHoveredId(id);

    if (!id) {
      setTooltipState(null);
      return;
    }

    // Cells without a tooltip never call onMouseMove — clear any stale card.
    const item = data.find((d) => d.id === id);
    if (!item?.tooltip) {
      setTooltipState(null);
    }
  };

  const handleMouseMove = (id: string, clientX: number, clientY: number) => {
    const item = data.find((d) => d.id === id);
    if (item?.tooltip) {
      setTooltipState({ content: item.tooltip, x: clientX, y: clientY });
    } else {
      setTooltipState(null);
    }
  };

  useLayoutEffect(() => {
    if (!tooltipState || !tooltipRef.current) {
      return;
    }

    setTooltipOffset(
      getTreeMapTooltipPosition(
        { x: tooltipState.x, y: tooltipState.y },
        {
          width: tooltipRef.current.offsetWidth,
          height: tooltipRef.current.offsetHeight,
        },
        {
          width: window.innerWidth,
          height: window.innerHeight,
          margin: TOOLTIP_VIEWPORT_MARGIN,
        }
      )
    );
  }, [tooltipState]);

  return (
    <Box
      component="div"
      className={clsx(treeMapClasses.root, className)}
      data-testid={testId}
      onMouseLeave={clearTooltip}
      sx={mergeSx(
        {
          lineHeight: 0,
          position: 'relative',
          width: '100%',
          height,
          // recharts renders ResponsiveContainer as inline-block by default;
          // verticalAlign removes the extra whitespace gap below it.
          '& .recharts-responsive-container': {
            verticalAlign: 'top',
          },
          // Focus-visible ring on the transparent click-target rect inside each cell.
          '& g[role=button]:focus-visible rect:last-of-type': {
            outline: '2px solid',
            outlineColor: 'currentColor',
            outlineOffset: '-2px',
          },
        },
        sx
      )}
    >
      <ResponsiveContainer width="100%" height="100%">
        <Treemap
          data={data}
          dataKey="value"
          nameKey="name"
          aspectRatio={ASPECT_RATIO}
          isAnimationActive={false}
          content={
            // recharts' TreemapContentType only types the base TreemapNode
            // shape, but spreads our TreeMapItem fields onto it at runtime —
            // the cast bridges that gap since recharts doesn't expose a
            // generic content prop.
            ((node: RechartsNodeWithItem) =>
              renderTreeMapCell({
                node,
                hoveredId,
                onHoverChange: handleHoverChange,
                onCellClick,
                onMouseMove: handleMouseMove,
                renderCell,
              })) as TreemapContentType
          }
        />
      </ResponsiveContainer>

      {tooltipState && (
        <Box
          ref={tooltipRef}
          component="div"
          data-testid="tree-map-tooltip-overlay"
          sx={{
            position: 'fixed',
            zIndex: (theme) => theme.zIndex.tooltip,
            // Passive overlay — actions belong on the cell, not the tooltip.
            pointerEvents: 'none',
            width: 'max-content',
            maxWidth: `calc(100vw - ${TOOLTIP_VIEWPORT_MARGIN * 2}px)`,
            left: tooltipOffset.left,
            top: tooltipOffset.top,
          }}
        >
          {tooltipState.content}
        </Box>
      )}
    </Box>
  );
};
